<template>
  <div class="w-full h-full flex flex-col overflow-hidden bg-dark-secondary">
    <div class="max-w-[1600px] mx-auto w-full px-6 lg:px-8 py-4 flex flex-col flex-1 min-h-0">

      <!-- Session Header (integrated style) -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
          <button
            @click="goBack"
            class="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl border border-dark bg-dark-tertiary hover:bg-dark-quaternary transition shadow-sm"
          >
            <ArrowLeft class="w-4 h-4 text-dark-secondary" />
          </button>
          <div class="min-w-0">
            <div class="flex items-center gap-3 mb-1">
              <h1 class="text-xl font-bold text-dark-primary tracking-tight">{{ sessionName }}</h1>
              <span
                v-if="sessionData?.risk"
                class="px-2 py-1 text-xs font-semibold rounded-md flex-shrink-0"
                :style="getRiskBadgeStyle(sessionData.risk)"
              >
                {{ sessionData.risk }}
              </span>
            </div>
            <div class="flex items-center gap-4 text-sm text-dark-secondary flex-wrap">
              <span v-if="sessionData?.uploaded_at" class="flex items-center gap-1">
                <Calendar class="w-3 h-3" />
                {{ formatSessionDate(sessionData.uploaded_at) }}
              </span>
              <span v-if="sessionDuration" class="flex items-center gap-1">
                <Clock class="w-3 h-3" />
                {{ sessionDuration }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button class="toolbar-btn" title="Share">
            <Share2 class="w-4 h-4" />
          </button>
          <button class="toolbar-btn" title="Settings">
            <Settings class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Tabs + layout bar -->
      <div class="flex items-center gap-3 mb-4 px-1">
        <div class="flex items-center gap-1 flex-1">
          <button
            v-for="tab in tabs" :key="tab.value"
            class="tab-btn"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
          >{{ tab.label }}</button>
        </div>

        <div class="flex items-center gap-1 flex-shrink-0">
          <button class="toolbar-btn" :class="{ 'text-accent': layoutMode === 'side' }" @click="layoutMode = 'side'" title="Side by side">
            <PanelRight class="w-4 h-4" />
          </button>
          <button class="toolbar-btn" :class="{ 'text-accent': layoutMode === 'bottom' }" @click="layoutMode = 'bottom'" title="Bottom panel">
            <PanelBottom class="w-4 h-4" />
          </button>
          <button class="toolbar-btn" :class="{ 'text-accent': layoutMode === 'full' }" @click="layoutMode = 'full'" title="Full screen">
            <Maximize2 class="w-4 h-4" />
          </button>
        </div>
      </div>

      <div class="flex-1 min-h-0 flex gap-6">
        
        <div 
          class="flex flex-col bg-dark-primary border border-dark rounded-xl shadow-sm overflow-hidden"
          :class="{
            'flex-1': layoutMode === 'side',
            'w-full': layoutMode === 'bottom' || layoutMode === 'full'
          }"
        >
          <div class="flex-1 min-h-0 relative bg-black">

            <!-- Status bar overlay (top full width) -->
            <div class="absolute top-0 left-0 right-0 z-20 pointer-events-none">
              <div class="bg-black/80 backdrop-blur-sm px-4 py-2 border-b border-white/10">
                <div class="flex items-center gap-1 text-xs text-white/90">
                  <span class="font-semibold text-white">{{ protocolLabel }}</span>
                  <span class="text-white/40">•</span>
                  <span class="text-white/60">DST</span>
                  <span class="font-semibold">{{ sessionData?.target_host || sessionData?.target_ip || '—' }}</span>
                  <span v-if="sessionData?.target_host && sessionData?.target_ip" class="text-white/60">({{ sessionData.target_ip }})</span>
                  <span v-if="sessionData?.pam_username" class="text-white/40 mx-1">•</span>
                  <span v-if="sessionData?.pam_username" class="text-white/60">USER</span>
                  <span v-if="sessionData?.pam_username" class="font-semibold">{{ sessionData.pam_username }}</span>
                  <span v-if="sessionData?.target_username" class="text-white/40 mx-1">•</span>
                  <span v-if="sessionData?.target_username" class="text-white/60">AS</span>
                  <span v-if="sessionData?.target_username" class="font-semibold">{{ sessionData.target_username }}</span>
                  <span v-if="sessionData?.origin_ip" class="text-white/40 mx-1">•</span>
                  <span v-if="sessionData?.origin_ip" class="text-white/60">SRC</span>
                  <span v-if="sessionData?.origin_ip" class="font-semibold">{{ sessionData.origin_ip }}</span>
                  <span class="text-white/40 mx-2">|</span>
                  <span class="text-white/60">RISK</span>
                  <span class="font-semibold" :class="riskColor(sessionData?.risk)">{{ sessionData?.risk?.toUpperCase() || '—' }}</span>
                  <span class="text-white/40 mx-1">•</span>
                  <span class="text-white/60">TTPs</span>
                  <span class="font-semibold" :style="(sessionData?.llm?.detections || 0) > 0 ? 'color:#22c55e' : 'color:#fff'">{{ sessionData?.llm?.detections || 0 }}</span>
                  <span class="text-white/40 mx-1">•</span>
                  <span class="text-white/60">CMDS</span>
                  <span class="font-semibold text-white">{{ sessionData?.guac?.commands || 0 }}</span>
                  <span class="text-white/40 mx-1">•</span>
                  <span class="text-white/60">FRMS</span>
                  <span class="font-semibold text-white">{{ sessionData?.vlm?.frames || sessionData?.ocr?.frames || 0 }}</span>
                </div>
              </div>
            </div>
            
            <div
              v-if="player.loading"
              class="absolute inset-0 flex items-center justify-center bg-black/70 z-50"
            >
              <div class="w-20 h-20 border-[6px] border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>

            <div
              v-if="player.error"
              class="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-white gap-3 z-50"
            >
              <AlertTriangle class="w-10 h-10 text-error" />
              <p class="text-sm">{{ player.error }}</p>
              <button
                class="px-4 py-2 bg-accent rounded text-white text-xs"
                @click="reloadSession"
              >
                Retry
              </button>
            </div>

            <div
              ref="viewport"
              class="w-full h-full overflow-hidden relative bg-black cursor-grab"
              @wheel="onWheelZoom"
              @mousedown="onMouseDown"
            >

              <!-- HLS video player -->
              <video
                v-show="!player.isGuacMode && !player.error"
                ref="videoEl"
                class="absolute inset-0 max-w-none h-full w-full object-contain"
                :style="contentStyle"
                muted
                preload="metadata"
                @loadstart="() => {}"
                @canplay="() => {}"
                @timeupdate="() => {}"
              />

              <!-- GUAC canvas mount -->
              <div
                v-show="player.isGuacMode && !player.error"
                ref="guacMount"
                class="absolute inset-0"
                :style="contentStyle"
              />

            </div>

            <!-- Player controls (bottom overlay) -->
            <div
              v-if="!player.error"
              class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4"
            >
              <!-- Progress bar (separate line) -->
              <div class="mb-3">
                <ProgressBar />
              </div>
              
              <!-- Controls row -->
              <div class="flex items-center gap-4">
                <!-- Play/Pause -->
                <button @click="togglePlay" class="control-btn text-white hover:text-accent">
                  <Play v-if="!player.isPlaying" class="w-6 h-6" />
                  <Pause v-else class="w-6 h-6" />
                </button>

                <!-- Skip back -->
                <button @click="skip(-10)" class="control-btn text-white hover:text-accent">
                  <SkipBack class="w-5 h-5" />
                </button>

                <!-- Skip forward -->
                <button @click="skip(10)" class="control-btn text-white hover:text-accent">
                  <SkipForward class="w-5 h-5" />
                </button>

                <!-- Time display -->
                <span class="text-sm text-white/80 font-mono whitespace-nowrap">
                  {{ formatTime(player.currentTime) }} / {{ formatTime(player.duration) }}
                </span>

                <!-- Spacer -->
                <div class="flex-1"></div>

                <!-- Volume -->
                <button @click="toggleMute" class="control-btn text-white hover:text-accent">
                  <Volume2 v-if="!isMuted" class="w-5 h-5" />
                  <VolumeX v-else class="w-5 h-5" />
                </button>

                <!-- Format selector -->
                <div class="relative">
                  <button
                    @click="showFormatMenu = !showFormatMenu"
                    class="control-btn text-white hover:text-accent"
                    title="Switch Format"
                  >
                    <Film class="w-5 h-5" />
                  </button>

                  <div
                    v-if="showFormatMenu"
                    @click.stop
                    class="absolute bottom-16 right-0 bg-black/60 backdrop-blur-sm rounded-md shadow-xl py-1 min-w-[100px] z-[60]"
                  >
                    <button
                      @click="switchFormat('hls')"
                      class="w-full text-left px-3 py-2 text-sm font-medium rounded-md hover:bg-white/10 transition-colors duration-150"
                      :class="currentFormat === 'hls' ? 'text-white bg-white/10' : 'text-white/80'"
                    >
                      <div class="flex items-center gap-2">
                        <Film class="w-4 h-4" />
                        <span>HLS</span>
                      </div>
                    </button>
                    <button
                      @click="switchFormat('guac')"
                      class="w-full text-left px-3 py-2 text-sm font-medium rounded-md hover:bg-white/10 transition-colors duration-150"
                      :class="currentFormat === 'guac' ? 'text-white bg-white/10' : 'text-white/80'"
                    >
                      <div class="flex items-center gap-2">
                        <Monitor class="w-4 h-4" />
                        <span>GUAC</span>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Zoom controls -->
                <div class="flex items-center gap-1">
                  <button @click="zoomStep(-0.2)" class="control-btn text-white hover:text-accent" :disabled="player.scale <= 0.25">
                    <ZoomOut class="w-4 h-4" />
                  </button>
                  <span class="text-xs text-white/60 font-mono min-w-[3ch]">{{ Math.round(player.scale * 100) }}%</span>
                  <button @click="zoomStep(0.2)" class="control-btn text-white hover:text-accent" :disabled="player.scale >= 3">
                    <ZoomIn class="w-4 h-4" />
                  </button>
                </div>

                <!-- Reset zoom -->
                <button @click="handleZoomReset" class="control-btn text-white hover:text-accent">
                  <RefreshCw class="w-4 h-4" />
                </button>

                <div class="w-px h-4 bg-dark mx-2"></div>

                <!-- Fullscreen -->
                <button class="control-btn" @click="toggleFullscreen">
                  <Maximize2 class="w-4 h-4" />
                </button>

              </div>
            </div>
          </div>

        </div>

        <div 
          v-if="layoutMode === 'side'"
          class="w-[400px] flex-shrink-0 flex flex-col bg-dark-primary border border-dark rounded-xl shadow-sm overflow-hidden"
          :style="loading ? 'opacity: 0.5; transition: opacity 0.2s' : 'opacity: 1; transition: opacity 0.2s'"
        >
          <OverviewPanel 
            v-if="activeTab === 'overview'" 
            :session="sessionData"
            @switch-tab="(tab) => activeTab = tab"
          />
          <AnalysisPanel
            v-else-if="activeTab === 'analysis'"
            :session-id="sessionId"
            @seek-frame="handleSeekFromAnalysis"
          />
          <CompliancePanel
            v-else-if="activeTab === 'compliance'"
            :session-id="sessionId"
          />
          <TimelinePanel
            v-else-if="activeTab === 'timeline'"
            :session-id="sessionId"
            :duration="sessionData?.guac?.duration_ms || 0"
            @seek-frame="handleSeekFromAnalysis"
            @switch-tab="(tab) => activeTab = tab"
          />
          <ChatPanel
            v-else-if="activeTab === 'chat'"
            :session-id="sessionId"
            @seek-frame="handleSeekFromAnalysis"
          />
          <div v-else class="flex-1 overflow-y-auto custom-scrollbar p-4">
            <div class="text-center py-20">
              <p class="text-sm text-dark-secondary">{{ activeTab }} panel</p>
              <p class="text-xs text-dark-tertiary mt-1">Content placeholder</p>
            </div>
          </div>
        </div>

      </div>
      <div 
        v-if="layoutMode === 'bottom'"
        class="h-[300px] flex-shrink-0 mt-6 bg-dark-primary border border-dark rounded-xl shadow-sm overflow-hidden"
        :style="loading ? 'opacity: 0.5; transition: opacity 0.2s' : 'opacity: 1; transition: opacity 0.2s'"
      >
        <OverviewPanel 
          v-if="activeTab === 'overview'" 
          :session="sessionData"
          @switch-tab="(tab) => activeTab = tab"
        />
        <AnalysisPanel
          v-else-if="activeTab === 'analysis'"
          :session-id="sessionId"
          @seek-frame="handleSeekFromAnalysis"
        />
        <CompliancePanel
          v-else-if="activeTab === 'compliance'"
          :session-id="sessionId"
        />
        <TimelinePanel
          v-else-if="activeTab === 'timeline'"
          :session-id="sessionId"
          :duration="sessionData?.guac?.duration_ms || 0"
          @seek-frame="handleSeekFromAnalysis"
          @switch-tab="(tab) => activeTab = tab"
        />
        <ChatPanel
          v-else-if="activeTab === 'chat'"
          :session-id="sessionId"
          @seek-frame="handleSeekFromAnalysis"
        />
        <div v-else class="h-full overflow-y-auto custom-scrollbar p-4">
          <div class="text-center py-20">
            <p class="text-sm text-dark-secondary">{{ activeTab }} panel</p>
            <p class="text-xs text-dark-tertiary mt-1">Content placeholder</p>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { 
  ArrowLeft, Monitor, Film, Share2,
  PanelRight, PanelBottom, Maximize2,
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  AlertTriangle, ZoomOut, ZoomIn, RefreshCw, Settings,
  Calendar, Clock, User, Server, Globe, Shield, Terminal, Image, Activity, UserCheck
} from "lucide-vue-next";

import { useSessionStore } from "../stores/sessionStore";
import { useAuthStore }    from "../stores/authStore";
import { usePlayerStore } from "../stores/playerStore";
import { apiClient } from "../services/apiClient";
import { createHlsPlayer } from "../services/hlsService";
import { createGuacPlayer } from "../services/guacService";
import ProgressBar from "./ProgressBar.vue";
import OverviewPanel    from "./OverviewPanel.vue";
import AnalysisPanel    from "./AnalysisPanel.vue";
import CompliancePanel  from "./CompliancePanel.vue";
import TimelinePanel from "./TimelinePanel.vue";
import ChatPanel from "./ChatPanel.vue";

const router = useRouter();
const route = useRoute();
const sessions  = useSessionStore();
const authStore = useAuthStore();
const player = usePlayerStore();

// Template refs
const videoEl = ref(null);
const guacMount = ref(null);
const viewport = ref(null);
const contentWrapper = ref(null);

// State
const sessionId = ref(route.params.id);
const sessionData = ref(sessions.currentSession ?? null);
const activeTab = ref("overview");
const layoutMode = ref(localStorage.getItem("investigation_layout") || "side");
const loading = ref(true);
const isMuted       = ref(false);
const showFormatMenu = ref(false);

// Drag state
const drag = ref({
  isDragging: false,
  startX: 0, startY: 0,
  initialX: 0, initialY: 0,
  moved: false,
});

// Player instances
let hlsInstance = null;
let guacInstance = null;
let currentId = null;

// Tabs
const tabs = [
  { label: "OVERVIEW",   value: "overview"   },
  { label: "ANALYSIS",   value: "analysis"   },
  { label: "COMPLIANCE", value: "compliance" },
  { label: "CHAT",       value: "chat"       },
  { label: "TIMELINE",   value: "timeline"   },
];

// Computed
const sessionName = computed(() => sessionData.value?.filename || sessionId.value);

const protocolLabel = computed(() => {
  if (!sessionData.value) return "—";
  const id = (sessionData.value.id ?? "").toLowerCase();
  const fn = (sessionData.value.filename ?? "").toLowerCase();
  if (fn.includes("ssh") || id.includes("ssh")) return "SSH";
  if (fn.includes("vnc") || id.includes("vnc")) return "VNC";
  return "RDP";
});

const currentFormat = computed(() => {
  return sessions.currentSession ? sessions.getSessionFormat(sessions.currentSession.id) : "hls";
});

const hasPAMContext = computed(() => {
  if (!sessionData.value) return false;
  return !!(
    sessionData.value.pam_username || 
    sessionData.value.target_username || 
    sessionData.value.target_host || 
    sessionData.value.target_ip || 
    sessionData.value.origin_ip
  );
});

const sessionDuration = computed(() => {
  // Use player duration as source of truth (matches what user sees in video controls)
  if (player.duration && player.duration > 0) {
    return formatTime(player.duration);
  }
  
  // Fallback to session metadata if player not loaded yet
  if (!sessionData.value) return null;
  const ms = sessionData.value.hls?.duration_ms || sessionData.value.guac?.duration_ms;
  return ms ? formatDuration(ms) : null;
});

const contentStyle = computed(() => ({
  transform: `translate(${player.posX}px, ${player.posY}px) scale(${player.scale})`,
  transformOrigin: "center center",
  transition:
    player.scale === 1 && player.posX === 0 && player.posY === 0
      ? "transform 0.3s ease-out"
      : "none",
  cursor: drag.value.isDragging ? "grabbing" : "grab",
}));

// Methods
function goBack() {
  router.push({ name: "dashboard" });
}

function getRiskBadgeStyle(risk) {
  const styles = {
    'CRITICAL': 'background-color: #dc2626; color: white; border: 1px solid #b91c1c;',
    'HIGH': 'background-color: #ef4444; color: white; border: 1px solid #dc2626;', 
    'MEDIUM': 'background-color: #f59e0b; color: white; border: 1px solid #d97706;',
    'LOW': 'background-color: #10b981; color: white; border: 1px solid #059669;'
  };
  return styles[risk?.toUpperCase()] || 'background-color: #6b7280; color: white;';
}

function formatSessionDate(timestamp) {
  if (!timestamp) return '—';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDuration(ms) {
  if (!ms) return "—";
  const t = Math.floor(ms / 1000);
  return `${Math.floor(t/60)}:${String(t%60).padStart(2,"0")}`;
}

function formatTime(seconds) {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function riskColor(risk) {
  const map = {
    critical: "text-error",
    high: "text-error",
    medium: "text-warning",
    low: "text-success"
  };
  return map[(risk ?? "").toLowerCase()] ?? "text-dark-secondary";
}

// Load session data
async function loadSessionData() {
  if (!sessionId.value) {
    player.setError("No session ID provided");
    return;
  }
  
  loading.value = true;
  
  try {
    // First try to get from store if available
    let session = sessions.sessions.find(s => s.id === sessionId.value);
    
    // If not in store (page reload), fetch from API
    if (!session) {
      console.log("[InvestigationView] Session not in store, fetching from API...");
      console.log("[InvestigationView] API Base:", authStore.apiBase);
      console.log("[InvestigationView] API Key:", authStore.apiKey ? 'configured' : 'missing');
      console.log("[InvestigationView] Session ID:", sessionId.value);
      
      // Check if auth is configured
      if (!authStore.apiBase || !authStore.apiKey) {
        throw new Error("API configuration missing - check Settings");
      }
      
      const res = await apiClient(`/sessions/${sessionId.value}`);
      
      // Debug: log the exact response
      console.log("[InvestigationView] Raw API response:", res);
      console.log("[InvestigationView] Response type:", typeof res);
      console.log("[InvestigationView] Response keys:", res ? Object.keys(res) : 'null');
      
      // Robust response handling
      if (!res) {
        throw new Error("Empty response from API");
      }
      
      // The API returns {data: {...}} structure, so extract the session data
      session = res.data;
      
      // If data is not in the expected structure, try other formats
      if (!session) {
        session = res.session || res;
      }
      
      // Debug: log the extracted session
      console.log("[InvestigationView] Extracted session:", session);
      console.log("[InvestigationView] Session type:", typeof session);
      
      // Final validation
      if (!session || typeof session !== 'object' || !session.id) {
        console.error("[InvestigationView] Invalid session data:", session);
        throw new Error("Invalid session data received from API");
      }
      
      console.log("[InvestigationView] Session fetched from API:", session.id);
    }
    
    // Update local and store state
    sessionData.value = session;
    sessions.selectSession(session);
    
    console.log("[InvestigationView] Session loaded successfully:", session.id);
    console.log("[InvestigationView] Current session in store:", sessions.currentSession?.id);
    console.log("[InvestigationView] Session format:", sessions.getSessionFormat(session.id));
    
  } catch (err) {
    console.error("[InvestigationView] Failed to load session:", err);
    console.error("[InvestigationView] Error details:", {
      message: err.message,
      name: err.name,
      stack: err.stack
    });
    
    // Better error messages
    let errorMsg = "Failed to load session data";
    if (err.message?.includes('API configuration missing')) {
      errorMsg = "API not configured - check Settings";
    } else if (err.message?.includes('404') || err.message?.includes('not found')) {
      errorMsg = `Session ${sessionId.value} not found`;
    } else if (err.message?.includes('401') || err.message?.includes('unauthorized')) {
      errorMsg = "Unauthorized access to session";
    } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
      errorMsg = "Network error - check connection";
    } else if (err.message?.includes('Empty response') || err.message?.includes('Invalid session data')) {
      errorMsg = `Invalid session response for ${sessionId.value}`;
    } else if (err instanceof TypeError && err.message?.includes('null')) {
      errorMsg = `Session ${sessionId.value} returned empty data`;
    }
    
    player.setError(errorMsg);
    
  } finally {
    loading.value = false;
  }
}

// Player controls
function togglePlay() {
  if (player.isGuacMode) {
    guacInstance?.togglePlay?.();
  } else if (videoEl.value) {
    videoEl.value.paused 
      ? videoEl.value.play().catch(() => {})
      : videoEl.value.pause();
  }
}

function skip(seconds) {
  const newTime = Math.max(0, Math.min(player.duration, player.currentTime + seconds));
  if (player.isGuacMode) guacInstance?.seek?.(newTime);
  else if (videoEl.value) videoEl.value.currentTime = newTime;
}

function toggleMute() {
  if (videoEl.value) {
    videoEl.value.muted = !videoEl.value.muted;
    isMuted.value = videoEl.value.muted;
  }
}

function toggleFullscreen() {
  const el = viewport.value?.parentElement;
  if (!el) return;
  document.fullscreenElement 
    ? document.exitFullscreen() 
    : el.requestFullscreen().catch(() => {});
}

// Zoom & Pan
function onMouseDown(e) {
  e.preventDefault();
  drag.value = {
    isDragging: true, startX: e.clientX, startY: e.clientY,
    initialX: player.posX, initialY: player.posY, moved: false,
  };
}

function onMouseMove(e) {
  if (!drag.value.isDragging) return;
  const dx = e.clientX - drag.value.startX;
  const dy = e.clientY - drag.value.startY;
  if (!drag.value.moved && Math.hypot(dx, dy) > 5) drag.value.moved = true;
  player.posX = drag.value.initialX + dx;
  player.posY = drag.value.initialY + dy;
}

function onMouseUp() {
  const { isDragging, moved } = drag.value;
  drag.value.isDragging = false;
  if (isDragging && !moved) togglePlay();
  drag.value.moved = false;
}

function onWheelZoom(e) {
  e.preventDefault();
  handleZoom(e.deltaY > 0 ? -0.1 : 0.1, e.clientX, e.clientY);
}

function handleZoom(delta, clientX, clientY) {
  const newScale = Math.max(0.25, Math.min(3, player.scale + delta));
  if (newScale === player.scale) return;
  const rect = viewport.value.getBoundingClientRect();
  const mouseX = (clientX ?? rect.width / 2 + rect.left) - rect.left;
  const mouseY = (clientY ?? rect.height / 2 + rect.top) - rect.top;
  const ratio = newScale / player.scale;
  player.posX -= (mouseX - rect.width / 2) * (ratio - 1);
  player.posY -= (mouseY - rect.height / 2) * (ratio - 1);
  player.scale = newScale;
}

function handleZoomReset() { player.resetZoom(); }

function zoomStep(delta) {
  const newScale = Math.max(0.25, Math.min(3, player.scale + delta));
  player.scale = newScale;
}

function handleSeekFromAnalysis(frameId) {
  const seekTime = frameId / 1000;
  const session  = sessions.currentSession;
  const hlsReady = session?.hls?.status === "completed";

  // Prefer HLS for seeking — it handles jumps natively
  // Only fall back to GUAC if HLS is not available
  if (hlsReady) {
    if (currentFormat.value !== "hls") {
      // Switch to HLS first, then seek once ready
      sessions.setSessionFormat(session.id, "hls");
      loadSession(seekTime, player.isPlaying);
    } else {
      videoEl.value.currentTime = seekTime;
    }
  } else if (guacInstance) {
    guacInstance.seek(frameId);
  }
}

function handleSeek(e) {
  const t = e.detail.time;
  if (player.isGuacMode) guacInstance?.seek?.(t);
  else if (videoEl.value) videoEl.value.currentTime = t;
}

// Cleanup
function cleanup() {
  if (hlsInstance) { try { hlsInstance.destroy(); } catch {} hlsInstance = null; }
  if (guacInstance) { try { guacInstance.disconnect(); } catch {} guacInstance = null; }
  if (videoEl.value) { try { videoEl.value.pause(); videoEl.value.src = ""; videoEl.value.load(); } catch {} }
  if (guacMount.value) { try { guacMount.value.innerHTML = ""; } catch {} }
  player.setPlaying(false);
  player.setCurrentTime(0);
}

// Load Player
async function loadSession(seekTime = null, autoPlay = false) {
  const session = sessions.currentSession;
  if (!session) return;
  const isNew = session.id !== currentId;
  if (isNew && currentId) player.clearCacheForSession(currentId);
  currentId = session.id;
  cleanup();
  player.setLoading(true);
  player.setError(null);
  player.setPlaying(false);
  player.setCurrentTime(0);

  const format = sessions.getSessionFormat(session.id);
  const base   = authStore.apiBase;
  const apiKey = authStore.apiKey;
  await nextTick();

  if (format === "hls") {
    player.isGuacMode = false;
    const src = `${base}/sessions/${session.id}/recording/hls`;
    hlsInstance = createHlsPlayer({
      video: videoEl.value, src, apiKey,
      onReady: () => { player.setLoaded(); player.resetZoom(); if (seekTime != null) videoEl.value.currentTime = seekTime; if (autoPlay) videoEl.value.play().catch(() => {}); },
      onPlay: () => player.setPlaying(true),
      onPause: () => player.setPlaying(false),
      onTime: (t) => player.setCurrentTime(t),
      onDuration: (d) => player.setDuration(d),
      onBuffered: (p) => player.setBufferedPct(p),
      onResolution: (w, h) => player.setResolution(w, h),
      onBitrate: (b) => player.setBitrate(b),
      onError: (msg) => player.setError(msg),
    });
    return;
  }

  player.isGuacMode = true;
  const src = `${base}/sessions/${session.id}/recording`;
  guacInstance = createGuacPlayer({
    mountPoint: guacMount.value, src, apiKey,
    onReady: () => { player.setLoaded(); player.resetZoom(); if (seekTime != null) guacInstance.seek(seekTime); if (autoPlay) setTimeout(() => guacInstance.togglePlay(), 100); },
    onPlay: () => player.setPlaying(true),
    onPause: () => player.setPlaying(false),
    onTime: (t) => player.setCurrentTime(t),
    onDuration: (d) => player.setDuration(d),
    onResolution: (w, h) => player.setResolution(w, h),
    onError: (msg) => player.setError(msg),
  });
}

function reloadSession() {
  loadSession(player.currentTime, false);
}

function switchFormat(format) {
  const wasPlaying = player.isPlaying;
  const currentTime = player.currentTime;
  const currentFmt = sessions.getSessionFormat(sessions.currentSession?.id);
  let seekTime = null;
  if (currentFmt === 'guac' && format === 'hls') seekTime = currentTime;
  if (sessions.currentSession) sessions.setSessionFormat(sessions.currentSession.id, format);
  showFormatMenu.value = false; // Close menu after selection
  loadSession(seekTime, wasPlaying);
}

watch(layoutMode, (mode) => localStorage.setItem("investigation_layout", mode));

// Watch for auth store to be ready after page reload
watch(() => authStore.hasBackend, async (hasBackend) => {
  console.log("[InvestigationView] Auth backend status changed:", hasBackend);
  if (hasBackend && !sessionData.value) {
    console.log("[InvestigationView] Auth is ready, retrying session load...");
    await loadSessionData();
    
    // After loading session data, initialize the player
    if (sessionData.value) {
      console.log("[InvestigationView] Session data loaded, checking currentSession...");
      console.log("[InvestigationView] Current session:", sessions.currentSession?.id);
      console.log("[InvestigationView] Session data:", sessionData.value?.id);
      
      await nextTick();
      
      if (sessions.currentSession) {
        console.log("[InvestigationView] Initializing player...");
        loadSession();
      } else {
        console.warn("[InvestigationView] currentSession not set, trying to set it manually");
        sessions.selectSession(sessionData.value);
        await nextTick();
        if (sessions.currentSession) {
          console.log("[InvestigationView] Player initialization after manual session select");
          loadSession();
        }
      }
    }
  }
});

onMounted(async () => {
  document.addEventListener("click", (e) => { 
    // Only close if clicked outside the format menu
    if (!e.target.closest('.relative')) {
      showFormatMenu.value = false; 
    }
  });
  
  // Only load session data if auth is already ready, otherwise wait for watch above
  if (authStore.hasBackend) {
    console.log("[InvestigationView] Auth already ready on mount");
    await loadSessionData();
  } else {
    console.log("[InvestigationView] Auth not ready on mount, waiting for credentials...");
  }
  
  if (sessions.currentSession) { 
    await nextTick(); 
    loadSession(); 
  }
  
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
  window.addEventListener("player-seek", handleSeek);
});

onUnmounted(() => {
  cleanup();
  if (currentId) player.clearCacheForSession(currentId);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
  window.removeEventListener("player-seek", handleSeek);
});
</script>

<style scoped>
.toolbar-btn {
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: transparent; border: none; border-radius: 6px;
  color: var(--text-secondary); cursor: pointer; transition: all 0.15s;
}
.toolbar-btn:hover:not(:disabled) { background: var(--hover-bg); color: var(--text-primary); }
.toolbar-btn:disabled { opacity: 0.3; cursor: default; }

.control-btn {
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px;
  background: transparent; border: none; border-radius: 5px;
  color: var(--text-secondary); cursor: pointer; transition: all 0.15s;
}
.control-btn:hover { background: var(--hover-bg); color: var(--text-primary); }

.tab-btn {
  position: relative; padding: 8px 16px; font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
  text-transform: uppercase; color: var(--text-tertiary); background: transparent; border: none;
  border-radius: 6px; cursor: pointer; transition: all 0.15s;
}
.tab-btn:hover { color: var(--text-secondary); background: var(--hover-bg); }
.tab-btn.active { color: var(--accent); background: rgba(var(--accent-rgb), 0.1); }

.custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
.custom-scrollbar::-webkit-scrollbar       { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
</style>